import React from 'react';
import usePageTranslation from '../../../shared/hooks/usePageTranslation';
import Navbar from '../../../shared/components/Navbar';
import Footer from '../../../shared/components/Footer';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import Card from '../../../shared/components/Card';

const Aide: React.FC = () => {
  const { t } = usePageTranslation('public', 'Aide');

  return (
    <div className="min-h-screen relative">
      <BackgroundImage />
      <Navbar />
      <div className="relative z-10">
        {/* Header de la page */}
        <section className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 text-gradient">
                {t('title')}
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                {t('subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Contenu principal */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Guide de démarrage */}
            <Card className="p-8 md:p-12 mb-8">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center">
                <span className="bg-green-500/20 text-green-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-green-500/50">
                  1
                </span>
                {t('gettingStarted.title')}
              </h2>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="p-6 bg-red-500/10 rounded-xl border-l-4 border-red-500">
                    <h3 className="text-xl font-semibold text-white mb-3">🔑 {t('gettingStarted.invitation.title')}</h3>
                    <ul className="space-y-2 text-gray-300">
                      {t('gettingStarted.invitation.items').map((item: string, index: number) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-6 bg-green-500/10 rounded-xl border-l-4 border-green-500">
                    <h3 className="text-xl font-semibold text-white mb-3">🎯 {t('gettingStarted.firstSteps.title')}</h3>
                    <ul className="space-y-2 text-gray-300">
                      {t('gettingStarted.firstSteps.items').map((item: string, index: number) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-6 bg-blue-500/10 rounded-xl border-l-4 border-blue-500">
                    <h3 className="text-xl font-semibold text-white mb-3">🏢 {t('gettingStarted.buildings.title')}</h3>
                    <ul className="space-y-2 text-gray-300">
                      {t('gettingStarted.buildings.items').map((item: string, index: number) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="p-6 bg-purple-500/10 rounded-xl border-l-4 border-purple-500">
                    <h3 className="text-xl font-semibold text-white mb-3">👥 {t('gettingStarted.recruitment.title')}</h3>
                    <ul className="space-y-2 text-gray-300">
                      {t('gettingStarted.recruitment.items').map((item: string, index: number) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>

                  <div className="p-6 bg-orange-500/10 rounded-xl border-l-4 border-orange-500">
                    <h3 className="text-xl font-semibold text-white mb-3">🚨 {t('gettingStarted.interventions.title')}</h3>
                    <ul className="space-y-2 text-gray-300">
                      {t('gettingStarted.interventions.items').map((item: string, index: number) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </Card>

            {/* Tutoriels avancés */}
            <Card className="p-8 md:p-12 mb-8">
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center">
                <span className="bg-blue-500/20 text-blue-400 w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold mr-4 border-2 border-blue-500/50">
                  2
                </span>
                {t('advanced.title')}
              </h2>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="p-6 bg-gradient-to-br from-red-500/10 to-pink-500/10 rounded-xl border border-red-500/30">
                  <div className="text-4xl mb-4">🔥</div>
                  <h3 className="text-xl font-semibold text-white mb-3">{t('advanced.fires.title')}</h3>
                  <p className="text-gray-400 mb-4">{t('advanced.fires.description')}</p>
                  <ul className="text-sm text-gray-300 space-y-1">
                    {t('advanced.fires.items').map((item: string, index: number) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>

                <div className="p-6 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl border border-blue-500/30">
                  <div className="text-4xl mb-4">🚑</div>
                  <h3 className="text-xl font-semibold text-white mb-3">{t('advanced.medical.title')}</h3>
                  <p className="text-gray-400 mb-4">{t('advanced.medical.description')}</p>
                  <ul className="text-sm text-gray-300 space-y-1">
                    {t('advanced.medical.items').map((item: string, index: number) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>

                <div className="p-6 bg-gradient-to-br from-yellow-500/10 to-orange-500/10 rounded-xl border border-yellow-500/30">
                  <div className="text-4xl mb-4">🚔</div>
                  <h3 className="text-xl font-semibold text-white mb-3">{t('advanced.security.title')}</h3>
                  <p className="text-gray-400 mb-4">{t('advanced.security.description')}</p>
                  <ul className="text-sm text-gray-300 space-y-1">
                    {t('advanced.security.items').map((item: string, index: number) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </Card>

            {/* Contact et support */}
            <Card className="p-8 md:p-12 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border-2 border-blue-500/50">
              <h2 className="text-3xl font-bold text-white mb-6">{t('help.title')}</h2>
              <p className="text-xl mb-8 text-gray-300">
                {t('help.description')}
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="bg-white/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{t('help.discord')}</h3>
                  <p className="text-gray-300 mb-4">{t('help.discordDesc')}</p>
                  <a
                    href="https://discord.gg/7wVa8EkvnP"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-primary-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                  >
                    {t('help.join')}
                  </a>
                </div>

                <div className="text-center">
                  <div className="bg-white/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{t('help.email')}</h3>
                  <p className="text-gray-300 mb-4">{t('help.emailDesc')}</p>
                  <a
                    href="mailto:support@opsecours.fr"
                    className="inline-block bg-primary-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                  >
                    {t('help.send')}
                  </a>
                </div>

                <div className="text-center">
                  <div className="bg-white/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">{t('help.faq')}</h3>
                  <p className="text-gray-300 mb-4">{t('help.faqDesc')}</p>
                  <a
                    href="/support/faq"
                    className="inline-block bg-primary-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                  >
                    {t('help.consult')}
                  </a>
                </div>
              </div>
            </Card>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default Aide;

